// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"

// Node controller — drag, select, resize, hidden, deletable, zIndex, dragHandle.
export default class extends Controller {
  static values = {
    id: String,
    type: { type: String, default: "default" },
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
    draggable: { type: Boolean, default: true },
    selectable: { type: Boolean, default: true },
    connectable: { type: Boolean, default: true },
    deletable: { type: Boolean, default: true },
    selected: { type: Boolean, default: false },
    hidden: { type: Boolean, default: false },
    zIndex: { type: Number, default: 0 },
    dragHandle: { type: String, default: "" },
    sourcePosition: { type: String, default: "right" },
    targetPosition: { type: String, default: "left" },
    parentId: { type: String, default: "" },
    extent: { type: Array, default: [] },
    expandParent: { type: Boolean, default: false },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 }
  }

  static outlets = ["ui--flow"]

  // Null-safe outlet access (perf pass): the outlet getter THROWS when an
  // outliving teardown (app.stop() after the canvas is removed) races a
  // Stimulus query — disconnect must never raise, its listeners outlive DOM.
  _flow() {
    try { return this.hasUiFlowOutlet ? this.uiFlowOutlet : null } catch (e) { return null }
  }

  connect() {
    this.element.style.left = `${this.xValue}px`
    this.element.style.top = `${this.yValue}px`
    // SSR size contract (node_component emits width/height values; the erb
    // comment promises "explicit node.width wins via inline style"): without
    // this the node renders at the CSS default (w-40 ≈ 160px) regardless of
    // the def — and a resizer whose min-height exceeds the rendered height
    // GROWS the node when the user tries to shrink it.
    if (this.widthValue) this.element.style.width = `${this.widthValue}px`
    if (this.heightValue) this.element.style.height = `${this.heightValue}px`
    this.element.dataset["ui-FlowNode"] = this.idValue
    this.element.dataset.nodeType = this.typeValue

    if (this.hiddenValue) this.element.style.display = "none"
    if (this.zIndexValue) this.element.style.zIndex = this.zIndexValue
    if (this.selectedValue) this.select()

    // Hover (spec gap closure ④): nodeenter/nodeleave bubble from the node to
    // the canvas root.
    this._enterHandler = () =>
      this.element.dispatchEvent(new CustomEvent("ui--flow:nodeenter", { detail: { nodeId: this.idValue }, bubbles: true }))
    this._leaveHandler = () =>
      this.element.dispatchEvent(new CustomEvent("ui--flow:nodeleave", { detail: { nodeId: this.idValue }, bubbles: true }))
    this.element.addEventListener("mouseenter", this._enterHandler)
    this.element.addEventListener("mouseleave", this._leaveHandler)

    // Keyboard a11y (spec §4.9): NODE_ACTIONS wires keydown server-side via a
    // Stimulus action string, but JS tests mount controllers directly, so the
    // JS listener is attached here too (guarded removal in disconnect, same
    // pattern as the hover listeners).
    this._keydownHandler = (e) => this._onKeydown(e)
    this.element.addEventListener("keydown", this._keydownHandler)

    // Register with the engine's element cache (perf): the flow controller
    // resolves node elements without querySelector on the drag/selection hot
    // paths; deregistration on disconnect keeps the cache garbage-free.
    this._flow()?._registerNodeElement?.(this.idValue, this.element)
  }

  // Live size sync (same contract the connect block applies): a value change
  // from any source re-applies the inline style. 0 = unset, leave the CSS.
  // A size change moves the handle anchors — the engine recomputes the
  // connected edge paths (mirrors the resizer path's noderesize broadcast).
  widthValueChanged() { if (this.widthValue) this.element.style.width = `${this.widthValue}px`; this._flow()?._refreshConnectedEdges?.(this.idValue) }
  heightValueChanged() { if (this.heightValue) this.element.style.height = `${this.heightValue}px`; this._flow()?._refreshConnectedEdges?.(this.idValue) }

  // --- Drag ---
  pointerDown(event) {
    // Form controls and buttons inside the node body own their pointer:
    // typing in an input or clicking a toolbar button must not start a node
    // drag (same selector the keydown guard below uses, plus button and the
    // [data-nodrag] opt-out).
    if (event.target.closest("input, textarea, select, button, [contenteditable], [data-nodrag]")) return
    if (!this.draggableValue || event.button !== 0) return

    // dragHandle restriction (React Flow parity): with a drag_handle declared,
    // only presses inside the matching element start a drag — body presses do
    // nothing here (selection still works through the separate click action).
    // Regression: the second clause tested closest("[data-ui--flow-node]"),
    // always truthy because the listener binds to the node root, so the guard
    // never fired and drag_handle was a silent no-op.
    if (this.dragHandleValue) {
      if (!event.target.closest(this.dragHandleValue)) return
    }

    event.stopPropagation()

    const flow = this._flow()
    if (!flow) return

    // Canvas-wide gate (U2): read-only blocks node dragging. The engine's
    // startNodeDrag already gates too — this stops the gesture one hop
    // earlier (no drag-offset bookkeeping, no dragging class) and keeps the
    // per-node flag stack readable in one place: canvas switch first, then
    // the node's own flag.
    if (!flow.interactive || flow.nodesDraggableValue === false) return

    if (flow.selectedNodes.has(this.idValue)) {
      flow.selectedNodes.forEach(id => {
        if (id === this.idValue) return
        const el = flow.hasNodesTarget ? flow.nodesTarget.querySelector(`[data-ui--flow-node="${id}"]`) : null
        if (!el) return
        el.dataset.dragOffsetX = (parseFloat(el.style.left) || 0) - this.xValue
        el.dataset.dragOffsetY = (parseFloat(el.style.top) || 0) - this.yValue
      })
    }

    this.element.classList.add("dragging")
    // Click-vs-drag tracking (React Flow parity): the press→up click handler
    // collapses the selection to this node — but ONLY when no drag happened,
    // or a group drag's mouseup click would discard the multi-selection.
    this._didDrag = false
    this._downPos = { x: event.clientX, y: event.clientY }
    flow.startNodeDrag(this.idValue, event)
    // Capture keeps the gesture on this element if the pointer leaves it; a
    // synthetic or already-released pointer has no capturable id and throws —
    // swallowed so the document listeners below still own the gesture.
    try { this.element.setPointerCapture(event.pointerId) } catch { /* gesture continues via document listeners */ }

    // Document-level gesture listeners (same pattern as handle_controller):
    // with capture, element listeners would also work, but a failed capture
    // (synthetic pointer, or capture raced a quick release) must not leak the
    // drag — an element-level up listener never fires when the pointer is
    // released outside the node, leaving classList "dragging" stuck on.
    this._moveHandler = (e) => this._onMove(e)
    this._upHandler = (e) => this._onUp(e)
    document.addEventListener("pointermove", this._moveHandler)
    document.addEventListener("pointerup", this._upHandler)
  }

  _onMove(event) {
    // Beyond a small jitter threshold the gesture is a drag, not a click.
    if (this._downPos && Math.abs(event.clientX - this._downPos.x) + Math.abs(event.clientY - this._downPos.y) > 3) this._didDrag = true
    this._flow()?.updateNodeDrag(event)
  }

  _onUp() {
    this.element.classList.remove("dragging")
    this._flow()?.endNodeDrag()
    document.removeEventListener("pointermove", this._moveHandler)
    document.removeEventListener("pointerup", this._upHandler)
  }

  // --- Click ---
  click(event) {
    event.stopPropagation()
    // A press that became a drag is not a click (React Flow parity): after a
    // group drag the selection must stay as the drag left it.
    if (this._didDrag) { this._didDrag = false; return }
    const flow = this._flow()
    if (!flow) return

    if (event.shiftKey) {
      flow.selectedNodes.has(this.idValue) ? flow.deselectNode(this.idValue) : flow.selectNode(this.idValue)
    } else {
      flow.clearSelection()
      flow.selectNode(this.idValue)
    }
  }

  dblclick(event) {
    event.stopPropagation()
    this._dispatchNodeEvent("nodedblclick", { nodeId: this.idValue })
  }

  // --- Selection ---
  // aria-pressed mirrors the selected class: the node root is a role=button
  // (T21 markup), and the class alone is invisible to assistive tech. Only
  // this controller's select/deselect paths are touched here — the flow
  // controller's own selection API is out of scope.
  select()   {
    if (this.selectableValue) { this.element.classList.add("selected"); this.selectedValue = true; this.element.setAttribute("aria-pressed", "true") }
  }
  deselect() {
    this.element.classList.remove("selected"); this.selectedValue = false; this.element.setAttribute("aria-pressed", "false")
  }

  // --- Position ---
  moveTo(x, y) {
    this.xValue = x; this.yValue = y
    this.element.style.left = `${x}px`
    this.element.style.top = `${y}px`
  }

  // --- Properties ---
  hide() { this.hiddenValue = true; this.element.style.display = "none" }
  show() { this.hiddenValue = false; this.element.style.display = "" }

  setZIndex(z) {
    this.zIndexValue = z
    this.element.style.zIndex = z
  }

  // --- Engine class/style tracking (NODE-REM; Item 16 node-side remainder) ---
  // Classes the engine adds via updateNode({class}) are recorded in
  // data-flow-engine-classes, so a later patch retracts exactly that set —
  // host classes (SSR chrome, caller presets) are never touched. The flow
  // controller's _patchNodeDom routes here once this controller is mounted
  // (same routing pattern _syncNodeHidden uses for hide()/show()); the
  // dataset keys mirror the edge side's updateEdge tracking.
  setEngineClasses(classStr) {
    ;(this.element.dataset.flowEngineClasses || "").split(/\s+/).filter(Boolean).forEach(c => this.element.classList.remove(c))
    const next = `${classStr ?? ""}`.split(/\s+/).filter(Boolean)
    next.forEach(c => this.element.classList.add(c))
    if (next.length) this.element.dataset.flowEngineClasses = next.join(" ")
    else delete this.element.dataset.flowEngineClasses
  }

  // String-form styles are replaced, never accumulated: the engine tracks the
  // property names it wrote (data-flow-engine-style-props) and retracts them
  // before applying the next string — a plain cssText += grows the attribute
  // with dead declarations on every updateNode({style}). Object-form assigns
  // per key (a key the caller empties retracts itself).
  setEngineStyle(style) {
    if (typeof style === "string") {
      const el = this.element
      ;(el.dataset.flowEngineStyleProps || "").split(" ").filter(Boolean).forEach(p => el.style.removeProperty(p))
      const probe = document.createElement("div")
      probe.style.cssText = style
      const props = []
      for (let i = 0; i < probe.style.length; i++) props.push(probe.style[i])
      props.forEach(p => el.style.setProperty(p, probe.style.getPropertyValue(p)))
      if (props.length) el.dataset.flowEngineStyleProps = props.join(" ")
      else delete el.dataset.flowEngineStyleProps
    } else if (style && typeof style === "object") {
      Object.assign(this.element.style, style)
    }
  }

  get position() { return { x: this.xValue, y: this.yValue } }
  get center()   { return { x: this.xValue + this.element.offsetWidth / 2, y: this.yValue + this.element.offsetHeight / 2 } }
  get bounds()   { return { x: this.xValue, y: this.yValue, width: this.element.offsetWidth, height: this.element.offsetHeight } }

  _dispatchNodeEvent(name, detail) {
    // S8: dispatch on the NODE ELEMENT (bubbling), not document — the event
    // originates where React Flow parity expects (event.target = the node),
    // and canvas-scoped listeners see it. Document-level listeners still
    // receive it via bubbling (flow_a11y's nodefocus listener is one).
    this.element.dispatchEvent(new CustomEvent(`ui--flow:${name}`, { detail, bubbles: true }))
  }

  // Keyboard a11y (spec §4.9): arrows rove focus to the nearest node in the
  // direction; Enter/Space selects. Delete is handled by the flow controller's
  // document-level keydown.
  _onKeydown(event) {
    if (event.target.closest("input, textarea, select, button, [contenteditable]")) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      const flow = this._flow()
      if (!flow) return
      if (event.shiftKey) {
        flow.selectedNodes.has(this.idValue) ? flow.deselectNode(this.idValue) : flow.selectNode(this.idValue)
      } else {
        flow.clearSelection()
        flow.selectNode(this.idValue)
      }
      return
    }
    const dirs = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"]
    if (!dirs.includes(event.key)) return
    event.preventDefault()
    // U18: Alt+Arrow moves the selection (Alt+Shift+Arrow a larger step);
    // plain Arrow keeps roving focus between nodes.
    if (event.altKey) { this._moveByKey(event.key, event.shiftKey); return }
    this._roveFocus(event.key)
  }

  // Keyboard move (U18). A focused node that is not yet selected is selected
  // first, so the move (and the announcement) always has a subject. Step:
  // one snap-grid cell when snapping is on, else 10px; Shift multiplies by 5.
  _moveByKey(key, large) {
    const flow = this._flow()
    if (!flow || typeof flow.moveSelectionBy !== "function") return
    if (!flow.selectedNodes.has(this.idValue)) { flow.clearSelection(); flow.selectNode(this.idValue) }
    const [gx, gy] = flow.snapToGridValue ? flow.snapGridValue : [10, 10]
    const mult = large ? 5 : 1
    const vec = { ArrowUp: [0, -gy], ArrowDown: [0, gy], ArrowLeft: [-gx, 0], ArrowRight: [gx, 0] }[key]
    flow.moveSelectionBy(vec[0] * mult, vec[1] * mult)
  }

  // Geometric roving: among visible nodes with a positive projection on the
  // direction vector, focus the closest by Euclidean distance (centers in flow
  // space). DOM order breaks ties.
  _roveFocus(direction) {
    const flow = this._flow()
    if (!flow || !flow.hasNodesTarget) return
    const vec = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] }[direction]
    const centerOf = (el) => {
      const p = flow.flowPosition ? flow.flowPosition(el) : { x: parseFloat(el.style.left) || 0, y: parseFloat(el.style.top) || 0 }
      return { x: p.x + el.offsetWidth / 2, y: p.y + el.offsetHeight / 2 }
    }
    const me = centerOf(this.element)
    let best = null, bestDist = Infinity
    flow.nodesTarget.querySelectorAll("[data-ui--flow-node]").forEach(el => {
      if (el === this.element || !flow._nodeVisible(el)) return
      const c = centerOf(el)
      const dx = c.x - me.x, dy = c.y - me.y
      const along = dx * vec[0] + dy * vec[1]
      if (along <= 0) return
      const dist = Math.hypot(dx, dy)
      if (dist < bestDist) { bestDist = dist; best = el }
    })
    if (best) { best.focus(); this._panToNode(best); this._dispatchNodeEvent("nodefocus", { nodeId: best.dataset["ui-FlowNode"] }) }
  }

  // Viewport follow (a11y): roving can land on an off-screen node — pan the
  // flow outlet just enough to bring it into view (20% padding each side;
  // a node wider than the padded viewport is centered). Uses only the
  // outlet's public viewport state + panBy, so translate-extent clamping and
  // the viewportchange emit still apply. fitView({nodes: [...]}) does not
  // exist on the engine and no new engine API is added.
  _panToNode(el) {
    const flow = this._flow()
    if (!flow || !flow.element) return
    const rect = flow.element.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const p = flow.flowPosition ? flow.flowPosition(el) : { x: parseFloat(el.style.left) || 0, y: parseFloat(el.style.top) || 0 }
    const zoom = flow.viewport.zoom
    const padX = rect.width * 0.2
    const padY = rect.height * 0.2
    const left = flow.viewport.x + p.x * zoom
    const top = flow.viewport.y + p.y * zoom
    const right = left + el.offsetWidth * zoom
    const bottom = top + el.offsetHeight * zoom

    let dx = 0, dy = 0
    if (right - left >= rect.width - padX * 2) dx = rect.width / 2 - (left + right) / 2
    else if (left < padX) dx = padX - left
    else if (right > rect.width - padX) dx = rect.width - padX - right
    if (bottom - top >= rect.height - padY * 2) dy = rect.height / 2 - (top + bottom) / 2
    else if (top < padY) dy = padY - top
    else if (bottom > rect.height - padY) dy = rect.height - padY - bottom

    if (dx || dy) flow.panBy(dx, dy)
  }

  disconnect() {
    this._flow()?._deregisterNodeElement?.(this.idValue, this.element)
    document.removeEventListener("pointermove", this._moveHandler)
    document.removeEventListener("pointerup", this._upHandler)
    if (this._enterHandler) {
      this.element.removeEventListener("mouseenter", this._enterHandler)
      this.element.removeEventListener("mouseleave", this._leaveHandler)
    }
    if (this._keydownHandler) {
      this.element.removeEventListener("keydown", this._keydownHandler)
    }
  }
}
